import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hook';
import {
  getAllCompaniesDetails,
  getAssociateCompanies,
  getLocations
} from '../redux/features/inputModule.slice';
import { DEFAULT_OPTIONS_PAYLOAD } from '../components/common/Table';
import {
  EmployeeUploadFilterState,
  EmployeeUploadSearchConfig,
  EmployeeUploadConfig,
  INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE,
  INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG,
  buildEmployeeUploadQueryPayload,
  getEmployeeUploadResetFields
} from '../utils/employeeUploads.utils';

export const useEmployeeUploadFilters = () => {
  const [filters, setFilters] = useState<EmployeeUploadFilterState>(INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE);

  const updateFilter = useCallback((field: keyof EmployeeUploadFilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...getEmployeeUploadResetFields(field)
    }));
  }, []);

  const updateMultipleFilters = useCallback((updates: Partial<EmployeeUploadFilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE);
  }, []);

  return { filters, updateFilter, updateMultipleFilters, resetFilters, setFilters };
};

export const useEmployeeUploadSearchConfig = () => {
  const [searchConfig, setSearchConfig] = useState<EmployeeUploadSearchConfig>(INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG);

  const updateSearchConfig = useCallback((updates: Partial<EmployeeUploadSearchConfig>) => {
    setSearchConfig(prev => ({ ...prev, ...updates }));
  }, []);

  const resetSearchConfig = useCallback(() => {
    setSearchConfig(INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG);
  }, []);

  return { searchConfig, updateSearchConfig, resetSearchConfig };
};

export const useEmployeeUploadData = (config: EmployeeUploadConfig) => {
  const dispatch = useAppDispatch();

  const fetchData = useCallback((
    dispatchAction: any,
    filters: EmployeeUploadFilterState,
    searchConfig: EmployeeUploadSearchConfig,
    overrides: Partial<any> = {},
    sortField?: string,
    sortDirection?: 'asc' | 'desc'
  ) => {
    const payload = buildEmployeeUploadQueryPayload(filters, searchConfig, config, overrides, sortField, sortDirection);
    dispatch(dispatchAction(payload));
  }, [dispatch, config]);

  const fetchWithSearch = useCallback((
    dispatchAction: any,
    filters: EmployeeUploadFilterState,
    searchConfig: EmployeeUploadSearchConfig,
    searchInput: string
  ) => {
    const payload = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      config,
      { [config.searchFieldName]: searchInput }
    );
    dispatch(dispatchAction(payload));
  }, [dispatch, config]);

  return { fetchData, fetchWithSearch };
};

export const useEmployeeUploadCompanyData = () => {
  const dispatch = useAppDispatch();
  const companiesDetails = useAppSelector((state) => state.inputModule.companiesDetails);
  const associateCompaniesDetails = useAppSelector((state) => state.inputModule.associateCompaniesDetails);
  const locationsDetails = useAppSelector((state) => state.inputModule.locationsDetails);

  const companies = ((companiesDetails || {}).data || {}).items || [];
  const associateCompanies = ((associateCompaniesDetails || {}).data || {}).items || [];
  const locations = ((locationsDetails || {}).data || {}).items || [];

  const fetchCompanies = useCallback(() => {
    const payload = { ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_parent_eq: true, is_active_eq: true } };
    dispatch(getAllCompaniesDetails(payload));
  }, [dispatch]);

  const fetchAssociateCompanies = useCallback((companyId: string) => {
    const payload = {
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { is_parent_eq: false, parent_company_id_eq: companyId }
    };
    dispatch(getAssociateCompanies(payload));
  }, [dispatch]);

  const fetchLocations = useCallback((associateCompanyId: string) => {
    const payload = {
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { company_id_eq: associateCompanyId },
      includes: {
        location: { city: ['state'] }
      },
      include: {
        location: {
          only: ['id', 'name', 'code'],
          include: {
            city: {
              only: ['id', 'name', 'code'],
              include: {
                state: {
                  only: ['id', 'name', 'code']
                }
              }
            }
          }
        }
      },
      sorts: [`location_name asc`]
    };
    dispatch(getLocations(payload));
  }, [dispatch]);

  return {
    companies,
    associateCompanies,
    locations,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations
  };
};